import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputProps,
  Stack,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";
import _ from "lodash";

import AppTypography from "@/common/components/Text/AppTypography";
import { MemoHelper, memoWithReversedArgs } from "@/common/helpers/memo";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import { ValidationHelper } from "@/common/validation";
import { GeneralProductProfileInputDto } from "@/core/api/generated/v1";

import SubscriptionPlanPriceInput from "../../AssetSubscriptionPlan/SubscriptionPlanPriceInput";
import CountryAutocomplete from "../../ReferenceData/CountryAutocomplete";

interface Props {
  value: GeneralProductProfileInputDto | undefined;
  errors: FormikErrors<GeneralProductProfileInputDto> | undefined;
  inputsProps?: {
    /** Applied to all inputs */
    all?: {
      size?: InputProps["size"];
    };
  };
  onChange: (value: GeneralProductProfileInputDto) => void;
}

export default memoWithReversedArgs(
  MemoHelper.arePropsEqual.factory<Props>({
    isExcludeFunctionProps: true,
    excludeProps: (t) => [t.inputsProps.$path],
  }),
  function GeneralProductProfileInput({ value, inputsProps, errors, onChange }: Props) {
    const generalError = _.isString(errors) ? errors?.toString() : undefined;
    const typedErrors = _.isObject(errors) ? errors : undefined;

    const valueRef = useLatestRef(value);

    return (
      <Stack spacing={1}>
        {value && (
          <Stack spacing={1}>
            <Card variant='outlined' elevation={0}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography component='div' variant='subtitle1' sx={{ mb: 1 }}>
                    Base price *
                  </Typography>

                  <SubscriptionPlanPriceInput
                    currency={value?.currency}
                    value={value?.basePrice}
                    errors={errors?.basePrice}
                    inputsProps={inputsProps}
                    onChange={({ currency, newValue }) => {
                      onChange({
                        ...valueRef.current,
                        currency: currency,
                        basePrice: newValue,
                      });
                    }}
                  />

                  {typedErrors?.currency && (
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(typedErrors?.currency, {
                        isIncludeNested: false,
                      })}
                    </FormHelperText>
                  )}
                </Stack>
              </CardContent>
            </Card>
            <FormControl margin='dense' fullWidth>
              <CountryAutocomplete
                entity={value?.country || undefined}
                entityId={undefined}
                onChange={(newValue) => {
                  onChange({
                    ...valueRef.current,
                    country: newValue,
                  });
                }}
                textFieldProps={{
                  error: Boolean(errors?.country),
                  helperText: ValidationHelper.getFormikErrorsAsString(typedErrors?.country, {
                    isIncludeNested: false,
                  }),
                }}
              />
            </FormControl>
            <FormControl margin='none'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value?.isAllowPropagation ?? false}
                    onChange={(e) => {
                      onChange({
                        ...valueRef.current,
                        isAllowPropagation: e.target.checked,
                      });
                    }}
                  />
                }
                label={
                  <AppTypography
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title:
                        "Whether to allow propagated changes to override local data. For instance, changes from asset subscription plan can propagate and override current local data.",
                    }}
                  >
                    Allow propagation
                  </AppTypography>
                }
              />
            </FormControl>
            {generalError && <FormHelperText error>{generalError}</FormHelperText>}{" "}
          </Stack>
        )}
      </Stack>
    );
  },
);
